/**
 * 1-click SDK exporter.
 *
 * Generates a strongly-typed TypeScript module (`prompts.ts`) or a Python
 * module (`prompts.py`) containing the winning prompt version plus typed
 * helpers for `{{variable}}` interpolation.
 */

import { camelCase, slugify, toCamel } from './util';

export type ExportLanguage = 'typescript' | 'python';

export interface ExportInput {
  promptName: string;
  content: string;
  variables: string[];
  version: number;
  commitMessage?: string;
  exportedAt?: number;
}

const GENERATED_HEADER = 'Generated by PromptVault Git — do not edit by hand.';

function escapeTemplateLiteral(source: string): string {
  return source.replace(/\\/g, '\\\\').replace(/`/g, '\\`').replace(/\$\{/g, '\\${');
}

function escapePythonString(source: string): string {
  return source.replace(/\\/g, '\\\\').replace(/"""/g, '\\"""');
}

export function exportFilename(language: ExportLanguage, promptName: string): string {
  return language === 'typescript' ? `${slugify(promptName)}_prompts.ts` : `${slugify(promptName)}_prompts.py`;
}

/**
 * Strongly-typed TypeScript module with an interpolation helper.
 */
export function generateTypeScript(input: ExportInput): string {
  const typeName = `${toPascal(input.promptName)}PromptVariables`;
  const varLines = input.variables.length
    ? input.variables.map((name) => `  /** {{${name}}} */\n  ${name}: string;`).join('\n')
    : '  /** This prompt takes no variables. */\n  readonly _?: never;';
  const constLines = input.variables.length
    ? `export const PROMPT_VARIABLES = [\n${input.variables.map((n) => `  "${n}",`).join('\n')}\n] as const;`
    : 'export const PROMPT_VARIABLES = [] as const;';

  return `// ${GENERATED_HEADER}
// Prompt: ${input.promptName}
// Commit: v${input.version}${input.commitMessage ? ` — "${input.commitMessage}"` : ''}
// Exported: ${new Date(input.exportedAt ?? Date.now()).toISOString()}

export type ${typeName} = {
${varLines}
};

export type PromptVariable = (typeof PROMPT_VARIABLES)[number];

export const PROMPT_VERSION = ${input.version} as const;
${constLines}

/** The prompt template exactly as committed (v${input.version}). */
export const systemPrompt = \`${escapeTemplateLiteral(input.content)}\`;

const SLOT_RE = /\\{\\{\\s*([A-Za-z_][A-Za-z0-9_.\\-]*)\\s*\\}\\}/g;

/**
 * Interpolate \`{{variable}}\` slots. Missing values resolve to "".
 */
export function renderPrompt(variables: Partial<${typeName}> = {}): string {
  return systemPrompt.replace(SLOT_RE, (_match, key: string) => {
    const value = (variables as Record<string, string | undefined>)[key];
    return value === undefined || value === null ? "" : String(value);
  });
}

/**
 * Build a chat-completions style request body for the prompt template.
 */
export function buildChatRequest(
  variables: Partial<${typeName}>,
  userMessage: string
): { model: string; messages: Array<{ role: "system" | "user"; content: string }> } {
  return {
    model: "gpt-4o",
    messages: [
      { role: "system", content: renderPrompt(variables) },
      { role: "user", content: userMessage },
    ],
  };
}
`;
}

/**
 * Python module with an interpolation helper (`render_prompt`).
 */
export function generatePython(input: ExportInput): string {
  const fnName = snakeCase(input.promptName);
  const tupleVars = input.variables.length
    ? `(${input.variables.map((v) => `'${v}'`).join(', ')}${input.variables.length === 1 ? ',' : ''})`
    : '()';
  const docVars = input.variables.length ? input.variables.join(', ') : 'none';

  return `"""${GENERATED_HEADER}

Prompt: ${input.promptName}
Commit: v${input.version}${input.commitMessage ? ` — "${input.commitMessage}"` : ''}
Exported: ${new Date(input.exportedAt ?? Date.now()).toISOString()}
Variables: ${doc_vars(docVars)}
"""

from __future__ import annotations

import re
from typing import Mapping, Optional

PROMPT_VERSION = ${input.version}
PROMPT_VARIABLES = ${tupleVars}

SYSTEM_PROMPT = """${escapePythonString(input.content)}"""

_VARIABLE_RE = re.compile(r"\\{\\{\\s*([A-Za-z_][A-Za-z0-9_.\\-]*)\\s*\\}\\}")


def ${fnName}(variables: Optional[Mapping[str, str]] = None) -> str:
    """Interpolate ``{{variable}}`` slots. Missing values resolve to ``""``."""
    values = dict(variables or {})

    def _replace(match: "re.Match[str]") -> str:
        value = values.get(match.group(1))
        return "" if value is None else str(value)

    return _VARIABLE_RE.sub(_replace, SYSTEM_PROMPT)


def ${fn_name_alias(fnName)}(variables: Optional[Mapping[str, str]], user_message: str) -> "dict":
    """Build an OpenAI-compatible chat request body for this prompt."""
    return {
        "model": "gpt-4o",
        "messages": [
            {"role": "system", "content": ${fnName}(variables)},
            {"role": "user", "content": user_message},
        ],
    }
`;
}

/** `Support Triage` → `SupportTriage` */
export function toPascal(input: string): string {
  const camel = toCamel(input);
  return camel.charAt(0).toUpperCase() + camel.slice(1);
}

/** `Support Triage System` → `support_triage_system` */
export function snakeCase(input: string): string {
  return slugify(input);
}

/** Alias builder name so the module exposes `build_chat_request` next to `support_triage_system`. */
export function fn_name_alias(fnName: string): string {
  return `build_${fnName.replace(/_prompt$/, '')}_request`.replace(/__+/g, '_');
}

function doc_vars(vars: string): string {
  return vars;
}
